import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { useDialogController } from '@machobear/design-system'
import { isEmpty } from 'lodash'

import { agreeToAgreements } from '@/ports'
import useCurrentSession from '@/hooks/use-current-session'
import useSessionComplete from '@/hooks/use-session-complete'
import { useGetUserAgreementsMismatched } from '@/features/Agreements/use-get-user-agreements'
import { useDSLogin } from '@/features/Auth/Context/ds-login-provider'

import TermsDialog from '.'
import queryClient from '../query-client'

const MANDATORY_TERMS_LIST = ['privacy_policy', 'terms_conditions']

type ContextType = {
  showModal: () => void
  closeModal: () => void
  mismatched: any
  latestAgreements: any
  isLoading: boolean
  showModalForRequiredTerms: (id: string, action: () => void) => void
  checkAgreementMismatch: (id: string) => boolean
}

const TermsDialogContext = createContext<ContextType | undefined>(undefined)

interface TermsDialogProviderProps {
  children: ReactNode
}

export const TermsDialogProvider: React.FC<TermsDialogProviderProps> = ({
  children
}) => {
  const isLoggedIn = useSessionComplete()
  const { setAfterLogin } = useDSLogin()
  const { showModal, closeModal, visible } = useDialogController({
    stopPropagation: true
  })
  const onLoadAgreementsCallback = useRef<((data: any) => void) | null>(null)
  const onTermAcceptCallback = useRef<(() => void) | null>(null)
  const session = useCurrentSession()
  const userId = session?.id

  const [mismatchedData, setMismatchedData] = useState<Record<
    string,
    any
  > | null>(null)
  const [actionRequiredTerms, setActionRequiredTerms] = useState<string | null>(
    null
  )
  const { mismatched, latestAgreements, isLoading, isFetching, isFetched } =
    useGetUserAgreementsMismatched({
      enabled: isLoggedIn,
      onSuccess: data => {
        setMismatchedData(data.mismatched)
        if (onLoadAgreementsCallback.current) {
          onLoadAgreementsCallback.current(data.mismatched)
          onLoadAgreementsCallback.current = null
        }
      }
    })

  const showModalForRequiredTerms = (id: string, action: () => void) => {
    function check(data: any) {
      if (data?.[id]) {
        setActionRequiredTerms(id)
        onTermAcceptCallback.current = action
        showModal()
      } else {
        action()
      }
    }

    if (isFetching || (!isFetched && !mismatchedData)) {
      onLoadAgreementsCallback.current = check
      return
    }

    check(mismatchedData)
  }

  const checkAgreementMismatch = (id: string) => {
    return !isEmpty(mismatched) && Object.keys(mismatched).includes(id)
  }

  const mismatchedArray = !isLoading ? Object.keys(mismatched) : []

  const mismatchedMandatoryAgreements = mismatchedArray?.filter(mismatch =>
    MANDATORY_TERMS_LIST.includes(mismatch)
  )
  const mismatchedOptionalAgreements = mismatchedArray?.filter(
    mismatch => !MANDATORY_TERMS_LIST.includes(mismatch)
  )

  const showCloseButton = mismatchedMandatoryAgreements.length === 0

  const termsToAccept =
    mismatchedMandatoryAgreements.length > 0
      ? mismatchedMandatoryAgreements
      : actionRequiredTerms
      ? [actionRequiredTerms]
      : mismatchedOptionalAgreements

  const clearTermsAcceptAction = () => {
    setActionRequiredTerms(null)
    setAfterLogin(null)
  }

  const handleCloseModal = () => {
    clearTermsAcceptAction()
    closeModal()
  }

  const onActionRequiredTermAccept = () => {
    onTermAcceptCallback.current && onTermAcceptCallback.current()
    onTermAcceptCallback.current = null
  }

  const isTermsAcceptActionRegistered = !!onTermAcceptCallback.current

  const mandatoryAgreementsHasBennAccepted =
    mismatchedMandatoryAgreements.length === 0

  const onTermsAccept = async () => {
    const { response, body }: any = await agreeToAgreements({
      body: termsToAccept
    })

    if (response.ok) {
      queryClient.invalidateQueries({
        queryKey: ['user-agreements-mismatched', userId]
      })

      const hasActionRequiredTermsAccepted = actionRequiredTerms
        ? !!body.agreements?.[actionRequiredTerms]
        : false

      hasActionRequiredTermsAccepted && onActionRequiredTermAccept()

      if (
        !isTermsAcceptActionRegistered ||
        mandatoryAgreementsHasBennAccepted
      ) {
        return handleCloseModal()
      }

      return null
    } else {
      console.error('[agreement/agreeToAgreements] Error occured: ')
    }
  }

  useEffect(() => {
    if (mismatched) {
      setMismatchedData(mismatched)
    }
  }, [mismatched])

  useEffect(() => {
    if (!isLoggedIn && !isFetching) {
      setMismatchedData(null)
      queryClient.removeQueries({ queryKey: ['user-agreements-mismatched'] })
    }
  }, [isFetching, isLoggedIn])

  useEffect(() => {
    if (isLoggedIn && !isLoading && mismatchedMandatoryAgreements.length > 0) {
      showModal()
    }
  }, [isLoggedIn, isLoading, mismatchedMandatoryAgreements.length, showModal])

  return (
    <TermsDialogContext.Provider
      value={{
        showModal,
        closeModal,
        mismatched,
        latestAgreements,
        isLoading,
        showModalForRequiredTerms,
        checkAgreementMismatch
      }}
    >
      {children}
      <TermsDialog
        visible={visible}
        onTermsAccept={onTermsAccept}
        onClose={handleCloseModal}
        showCloseButton={showCloseButton}
        mismatchedMandatoryAgreements={mismatchedMandatoryAgreements}
        mismatchedOptionalAgreements={mismatchedOptionalAgreements}
        actionRequiredTerms={actionRequiredTerms}
      />
    </TermsDialogContext.Provider>
  )
}

export const useTermsDialogContext = (): ContextType => {
  const context = useContext(TermsDialogContext)
  if (!context) {
    throw new Error(
      'useTermsDialogContext must be used within a TermsDialogContextProvider'
    )
  }
  return context
}
